import { HoverableLinkSizes } from '@paywithextend/components'
import cn from 'classnames'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import React, { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import CardsIllustration from 'src/assets/images/cards-illustration.png'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { selectBrandName } from 'src/redux/selectors/session.selectors'

import style from './EditPhoneModal.style'

type EditPhoneModalProps = {
  className?: string
  isOpen: boolean
  initialPhoneIsoCountry: string
  initialPhone: string
  isSubmitting: boolean
  phoneError?: string
  onSubmit: (phoneIsoCountry: string, phone: string) => void
  onGoBack: () => void
  onClearError?: () => void
}

export default function EditPhoneModal(
  props: EditPhoneModalProps,
): ReactElement {
  const {
    className = '',
    isOpen,
    initialPhoneIsoCountry,
    initialPhone,
    isSubmitting,
    phoneError,
    onSubmit,
    onGoBack,
    onClearError,
  } = props

  const translate = useTranslate()
  const hideImages = useSelector(selectBrandName) === SupportedBrands.Jpm

  const [phoneIsoCountry, setPhoneIsoCountry] = useState(initialPhoneIsoCountry)
  const [phone, setPhone] = useState(initialPhone)
  const [localError, setLocalError] = useState<string | undefined>()

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (!phone || phone.trim().length === 0) {
      setLocalError(translate('Phone number is required'))
      return
    }
    setLocalError(undefined)
    onSubmit(phoneIsoCountry, phone)
  }

  const displayError = phoneError || localError

  return (
    <BaseModal
      className={cn('EditPhoneModal', className)}
      isOpen={isOpen}
      onClose={onGoBack}
      showCloseButton={false}
      css={style}
      aria-label={translate('Edit Phone Number')}
    >
      {!hideImages && (
        <img
          className="edit-phone-decorative-image"
          src={CardsIllustration}
          alt=""
          role="presentation"
        />
      )}
      <form
        className="edit-phone-form"
        onSubmit={handleSubmit}
        data-testid="editPhoneModalForm"
      >
        <h2 className="edit-phone-title">{translate('Edit Phone Number')}</h2>

        <p className="edit-phone-subtitle">
          {translate("Enter your phone number and we'll send you a new code.")}
        </p>

        <div>
          <FieldLabel>{translate('Phone Number')}</FieldLabel>
          <div className="edit-phone-input-row">
            <CountryPhoneDropdown
              id="editPhone"
              selectedValue={phoneIsoCountry}
              chooseOption={(val: string): void => setPhoneIsoCountry(val)}
            />
            <PhoneTextInputField
              id="editPhone"
              css={`
                flex-grow: 2;
              `}
              value={phone}
              countryIso={phoneIsoCountry}
              onChange={(val: string): void => {
                setPhone(val)
                setLocalError(undefined)
                onClearError?.()
              }}
              errors={displayError ? [displayError] : undefined}
            />
          </div>
        </div>

        <Button
          id="editPhoneModalBtn"
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          text={translate('Send New Code')}
          loading={isSubmitting}
          disabled={isSubmitting}
          data-testid="editPhoneModalBtn"
          big
        />

        <div className="edit-phone-actions">
          <HoverableLink
            underline
            size={HoverableLinkSizes.Medium}
            onClick={onGoBack}
            ariaLabelSource="Go back"
            testId="editPhoneGoBack"
          >
            {translate('Go back')}
          </HoverableLink>
        </div>
      </form>
    </BaseModal>
  )
}
